// import api's and mutations
import { mutations, queryKeys } from 'RestAPIs/rapini';

// import components
import { LoadingButton } from '@mui/lab';
import { Box, Button } from '@mui/material';
import {
  ControlledSelect,
  ControlledTextField,
  ControlledTimePicker,
  Drawer,
  showSnackbar
} from 'components/shared';
import ControlledFunctionSelector from 'components/shared/ControlledFields/ControlledFunctionSelector';
import ControlledMonetaryField from 'components/shared/ControlledFields/ControlledMonetaryField';
import ControlledPercentageField from 'components/shared/ControlledFields/ControlledPercentageField';
import ControlledValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';
import ControlledWeekdaysPicker from 'components/shared/ControlledFields/ControlledWeekdaysPicker';
import DrawerFooter from 'components/shared/Drawer/DrawerFooter';
import { StyledMainBox } from './AddEditCostCalculationExternalSurcharge.styles';
import { StyledBoxHalfWidth } from '../Box/Box.styles';

// import hooks
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
// import utils
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs, { Dayjs } from 'dayjs';

// import types
import { CostCalculationExternalSurcharge } from 'generated';
import { InferType } from 'yup';
import { Option } from 'components/shared/ControlledFields/ControlledSelect';

// import schema
import costCalculationExternalsSurchargeSchema from './cost-calculation-external-surcharge-schema';
import { AddEditCostCalculationExternalSurchargesProperties } from './AddEditCostCalculationExternalSurcharge.types';
import { GridContainer, GridCompact6 } from '../Grid/Grid.components';

const schema = costCalculationExternalsSurchargeSchema();
type Form = InferType<typeof schema>;
const minTime = dayjs('1969-12-31T23:00:00Z');
const maxTime = dayjs('1970-01-01T23:00:00Z');

export function AddEditCostCalculationExternalSurcharge({
  employmentId,
  organizationalUnitId,
  isOpen,
  surcharge,
  handleClose
}: AddEditCostCalculationExternalSurchargesProperties) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const defaultValues = {
    description: surcharge?.description,
    validFrom: dayjs(surcharge?.validFrom),
    validTo: dayjs(
      surcharge?.validTo || dayjs().startOf('year').add(1, 'year')
    ),
    functionId: surcharge?.functionId,
    weekdays: {
      monday: surcharge?.monday || false,
      tuesday: surcharge?.tuesday || false,
      wednesday: surcharge?.wednesday || false,
      thursday: surcharge?.thursday || false,
      friday: surcharge?.friday || false,
      saturday: surcharge?.saturday || false,
      sunday: surcharge?.sunday || false
    },
    timeFrom: dayjs(surcharge?.timeFrom || minTime),
    timeTo: dayjs(surcharge?.timeTo || maxTime),
    surchargeType: surcharge?.surchargeType || 'AMOUNT',
    amount: surcharge?.amount || 0,
    percentage: surcharge?.percentage ? surcharge?.percentage * 100 : 100
  };

  const [currentSurchargeType, setSurchargeType] = useState<string>(
    surcharge?.surchargeType || 'AMOUNT'
  );

  const { control, handleSubmit, reset, setValue, watch } = useForm<Form>({
    resolver: yupResolver(schema),
    defaultValues
  });

  const validFrom = watch('validFrom');
  const validTo = watch('validTo');

  const surchargeTypeOptions: Option[] = [
    {
      value: 'PERCENTAGE',
      label: t('percentage')
    },
    {
      value: 'AMOUNT',
      label: t('fixedAmount')
    }
  ];

  useEffect(() => {
    reset(defaultValues);
    setSurchargeType(defaultValues.surchargeType);
  }, [surcharge]);

  const {
    mutate: createCostCalculationExternalSurcharge,
    isLoading: isCreateLoading
  } = mutations.useCreateCostCalculationExternalSurcharges({
    onError: (e: any) => {
      showSnackbar({
        severity: 'error',
        message: t(e?.response?.data?.error?.message || 'failed')
      });
    },
    onSuccess: () => {
      handleClose();
      queryClient.invalidateQueries(
        queryKeys.getCostCalculationExternalSurcharges(
          employmentId,
          organizationalUnitId
        )
      );
      showSnackbar({
        severity: 'success',
        message: t('addEditCostCalculationSurchargeSuccess')
      });
    }
  });

  const {
    mutate: updateCostCalculationExternalSurcharge,
    isLoading: isUpdateLoading
  } = mutations.useUpdateCostCalculationExternalSurcharges({
    onError: (e: any) => {
      showSnackbar({
        severity: 'error',
        message: t(e?.response?.data?.error?.message || 'failed')
      });
    },
    onSuccess: () => {
      handleClose();
      queryClient.invalidateQueries(
        queryKeys.getCostCalculationExternalSurcharges(
          employmentId,
          organizationalUnitId
        )
      );
      showSnackbar({
        severity: 'success',
        message: t('addEditCostCalculationSurchargeSuccess')
      });
    }
  });

  const onSubmit = async (data: Form) => {
    if (surcharge) {
      updateCostCalculationExternalSurcharge({
        id: surcharge.id,
        employmentId,
        organizationalUnitId,
        functionId: data.functionId,
        surchargeType: data.surchargeType,
        validFrom: data.validFrom.format('YYYY-MM-DD'),
        validTo: data.validTo.format('YYYY-MM-DD'),
        timeFrom: data.timeFrom.toISOString(),
        timeTo: data.timeTo.toISOString(),
        description: data.description,
        monday: data.weekdays.monday,
        tuesday: data.weekdays.tuesday,
        wednesday: data.weekdays.wednesday,
        thursday: data.weekdays.thursday,
        friday: data.weekdays.friday,
        saturday: data.weekdays.saturday,
        sunday: data.weekdays.sunday,
        amount: data.amount,
        percentage: data.percentage ? data.percentage / 100.0 : undefined
      } as CostCalculationExternalSurcharge);
    } else {
      createCostCalculationExternalSurcharge({
        employmentId,
        organizationalUnitId,
        functionId: data.functionId,
        surchargeType: data.surchargeType,
        validFrom: data.validFrom.format('YYYY-MM-DD'),
        validTo: data.validTo.format('YYYY-MM-DD'),
        timeFrom: data.timeFrom.toISOString(),
        timeTo: data.timeTo.toISOString(),
        description: data.description,
        monday: data.weekdays.monday,
        tuesday: data.weekdays.tuesday,
        wednesday: data.weekdays.wednesday,
        thursday: data.weekdays.thursday,
        friday: data.weekdays.friday,
        saturday: data.weekdays.saturday,
        sunday: data.weekdays.sunday,
        amount: data.amount,
        percentage: data.percentage ? data.percentage / 100.0 : undefined
      } as CostCalculationExternalSurcharge);
    }
  };

  function handleSelectSurchargeType(value: string) {
    setSurchargeType(value);
  }

  function onChangeTimeFrom(date: Dayjs | null) {
    setValue('timeFrom', date || minTime);
  }
  function onChangeTimeTo(date: Dayjs | null) {
    while (date?.isAfter(maxTime)) {
      date = date.subtract(1, 'day');
    }
    if (date?.isSame(minTime)) {
      date = maxTime;
    }
    setValue('timeTo', date || maxTime);
  }

  return (
    <>
      <Drawer
        open={isOpen}
        title={t(
          surcharge
            ? 'editCostCalculationSurcharge'
            : 'addCostCalculationSurcharge'
        )}
        onClose={handleClose}
      >
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <StyledMainBox>
            <ControlledValidFromValidTo
              control={control}
              validFromFieldName="validFrom"
              validToFieldName="validTo"
              validFromRequired
              validToRequired
            />

            <ControlledTextField
              control={control}
              name="description"
              label={t('description')}
              required
              fullWidth
            />
            {organizationalUnitId && (
              <ControlledFunctionSelector
                control={control}
                name="functionId"
                label={t('function')}
                startDate={validFrom}
                endDate={validTo}
                onlyPreferred
                organizationalUnitId={organizationalUnitId}
                onlySelectable
                fullWidth
              />
            )}
            <ControlledWeekdaysPicker control={control} name="weekdays" />
            <Box
              sx={{
                display: 'flex',
                gap: 2
              }}
            >
              <StyledBoxHalfWidth>
                <ControlledTimePicker
                  control={control}
                  label={t('timeFrom')}
                  name="timeFrom"
                  onChange={(date) => {
                    onChangeTimeFrom(date);
                  }}
                  fullWidth
                  baseDate={minTime}
                />
              </StyledBoxHalfWidth>
              <StyledBoxHalfWidth>
                <ControlledTimePicker
                  control={control}
                  label={t('timeTo')}
                  name="timeTo"
                  onChange={(date) => {
                    onChangeTimeTo(date);
                  }}
                  fullWidth
                  baseDate={minTime}
                />
              </StyledBoxHalfWidth>
            </Box>
            <GridContainer>
              <GridCompact6>
                <ControlledSelect
                  control={control}
                  label={t('surchargeType')}
                  name="surchargeType"
                  options={surchargeTypeOptions}
                  handleSelect={(value) =>
                    handleSelectSurchargeType(value as string)
                  }
                />
              </GridCompact6>
              <GridCompact6>
                {currentSurchargeType === 'AMOUNT' ? (
                  <ControlledMonetaryField
                    control={control}
                    label={t('baseAmount')}
                    name="amount"
                    required
                  />
                ) : (
                  <ControlledPercentageField
                    control={control}
                    label={t('percentage')}
                    name="percentage"
                    required
                  />
                )}
              </GridCompact6>
            </GridContainer>
          </StyledMainBox>
          <DrawerFooter>
            <Button onClick={handleClose} color="secondary" variant="contained">
              {t('cancel')}
            </Button>
            <LoadingButton
              loading={isCreateLoading || isUpdateLoading}
              type="submit"
              color="primary"
              variant="contained"
            >
              {t('save')}
            </LoadingButton>
          </DrawerFooter>
        </form>
      </Drawer>
    </>
  );
}
